// 常用查询。键名集中，失效时用同样的前缀。

import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { api } from "./api";
import type { GroupView, Me, OrgView, PolicyView, RobotState, RobotView, RoleView } from "./types";
import { useSession } from "./auth";

export const qk = {
  me: ["me"] as const,
  robots: (f?: object) => ["robots", f ?? {}] as const,
  robot: (id: string) => ["robots", "one", id] as const,
  policy: (id: string) => ["robots", "policy", id] as const,
  state: (id: string) => ["robots", "state", id] as const,
  groups: (org?: string | null) => ["groups", org ?? null] as const,
  users: (org?: string | null) => ["users", org ?? null] as const,
  bindings: (user: string) => ["bindings", user] as const,
  roles: (org?: string | null) => ["roles", org ?? null] as const,
  orgs: ["orgs"] as const,
};

export function useMe() {
  const authed = useSession((s) => s.authed);
  return useQuery({ queryKey: qk.me, queryFn: () => api<Me>("/v1/me"), enabled: authed, staleTime: 60_000 });
}

/** 平台管理员没有所属机构：机构相关页面用顶栏选择的机构。 */
export const useOrgPick = create<{ orgId: string | null; set: (id: string | null) => void }>((set) => ({
  orgId: null,
  set: (orgId) => set({ orgId }),
}));

/** 当前操作的机构：普通账号是自己的机构；平台账号是选中的机构（可能为空）。 */
export function useOrgId(): string | null {
  const me = useMe().data;
  const picked = useOrgPick((s) => s.orgId);
  return me?.user.org_id ?? picked;
}

/** 平台账号调机构接口时要带 `org_id`；普通账号不用带。 */
export function useOrgQuery(): { org_id?: string } {
  const me = useMe().data;
  const picked = useOrgPick((s) => s.orgId);
  return me && !me.user.org_id && picked ? { org_id: picked } : {};
}

export function useOrgs(enabled: boolean) {
  return useQuery({ queryKey: qk.orgs, queryFn: () => api<OrgView[]>("/v1/orgs"), enabled });
}

export function useRobot(id: string | undefined) {
  return useQuery({
    queryKey: qk.robot(id ?? ""),
    queryFn: () => api<RobotView>(`/v1/robots/${id}`),
    enabled: !!id,
  });
}

/** 在线状态、版本、设备能力（`capabilities`，按它决定显示哪些控件）。 */
export function useRobotState(id: string | undefined) {
  return useQuery({
    queryKey: qk.state(id ?? ""),
    queryFn: () => api<RobotState>(`/v1/robots/${id}/state`),
    enabled: !!id,
  });
}

/** `online`：机器人在线时，策略未被确认就轮询（离线的要等握手补发，不轮询）。 */
export function usePolicy(id: string | undefined, online = false) {
  return useQuery({
    queryKey: qk.policy(id ?? ""),
    queryFn: () => api<PolicyView>(`/v1/robots/${id}/policy`),
    enabled: !!id,
    // 改了策略后机器人回读前（在线下发通常 1 s 内）：轮询到一致为止
    refetchInterval: (q) =>
      online && q.state.data && q.state.data.policy_version_applied !== q.state.data.policy_version ? 5000 : false,
  });
}

export function useGroups() {
  const q = useOrgQuery();
  const me = useMe().data;
  const needOrg = !!me && !me.user.org_id;
  return useQuery({
    queryKey: qk.groups(q.org_id ?? me?.user.org_id),
    queryFn: () => api<GroupView[]>("/v1/robot-groups", { query: q }),
    enabled: !!me && (!needOrg || !!q.org_id),
  });
}

export function useRoles() {
  const q = useOrgQuery();
  const me = useMe().data;
  const needOrg = !!me && !me.user.org_id;
  return useQuery({
    queryKey: qk.roles(q.org_id ?? me?.user.org_id),
    queryFn: () => api<RoleView[]>("/v1/roles", { query: q }),
    enabled: !!me && (!needOrg || !!q.org_id),
  });
}
